/**
 * Utilidades para el cálculo de progreso y coincidencias de intercambio.
 */

/**
 * Calcula el progreso de una colección.
 * @param {Array} allStickers - Lista total de láminas.
 * @param {Object} collected - Objeto con las láminas obtenidas { number: true }.
 * @returns {Object} { total, have, pct }
 */
export function calculateProgress(allStickers, collected = {}) {
  const total = allStickers.length;
  if (total === 0) return { total: 0, have: 0, pct: 0 };
  
  const have = allStickers.filter(s => collected[s.number]).length;
  const pct = Math.round((have / total) * 100);
  
  return { total, have, pct };
}

/**
 * Calcula qué láminas me puede dar un amigo y cuáles le puedo dar yo.
 * @param {Object} myCollected - Mis láminas obtenidas.
 * @param {Object} myDuplicates - Mis láminas repetidas.
 * @param {Object} theirCollected - Láminas obtenidas del amigo.
 * @param {Object} theirDuplicates - Láminas repetidas del amigo.
 * @returns {Object} { give: [], receive: [], score: number }
 */
export function calculateMatches(myCollected = {}, myDuplicates = {}, theirCollected = {}, theirDuplicates = {}) {
  const give = [];
  const receive = [];

  // Lo que yo le puedo dar (mis repetidas que él no tiene)
  Object.keys(myDuplicates).forEach(num => {
    if (myDuplicates[num] > 0 && !theirCollected[num]) {
      give.push(num);
    }
  });

  // Lo que él me puede dar (sus repetidas que yo no tengo)
  Object.keys(theirDuplicates).forEach(num => {
    if (theirDuplicates[num] > 0 && !myCollected[num]) {
      receive.push(num);
    }
  });

  return {
    give,
    receive,
    score: give.length + receive.length
  };
}

/**
 * Calcula la rareza global basada en todos los usuarios.
 * @param {Array} allUsersData - Datos de todos los usuarios de la DB.
 * @param {Array} allStickers - Lista total de láminas.
 * @returns {Object} Mapa de { stickerNumber: percentage }
 */
export function calculateGlobalRarity(allUsersData, allStickers) {
  const counts = {};
  let totalUsers = allUsersData.length;

  allUsersData.forEach(userData => {
    const collected = userData.collected || {};
    Object.keys(collected).forEach(num => {
      counts[num] = (counts[num] || 0) + 1;
    });
  });

  const rarityMap = {};
  allStickers.forEach(s => {
    const count = counts[s.number] || 0;
    rarityMap[s.number] = totalUsers > 0 ? parseFloat(((count / totalUsers) * 100).toFixed(1)) : 0;
  });

  return rarityMap;
}

/**
 * Returns a category key for a sticker so that trade-balancing can
 * keep "same kind for same kind" (intro ↔ intro, museum ↔ museum,
 * team ↔ team) before falling back to cross-category swaps.
 *
 * The shape of category — i.e. `'section:intro'` vs `'team'` —
 * matters less than that the SAME logical bucket maps to the SAME
 * string for both sides of the trade.
 */
export function categoryOfSticker(sticker) {
  if (!sticker) return 'unknown';
  if (sticker.section) return `section:${sticker.section}`;
  // Players + team badges all collapse to "team". We don't split per
  // team because trading "MEX player ↔ MEX player" is unrealistic —
  // most trades are cross-team within Grupo etapa. Splitting by group
  // is too coarse to be useful either, so a single "team" bucket lets
  // group-stage stickers freely swap among themselves.
  return 'team';
}

/**
 * Sort-key tuple [categoryRank, rarity] so we always pull the rarest
 * sticker first WITHIN a category. Lower rarity = rarer = better.
 */
function rarityOf(rarityMap, num) {
  const r = rarityMap?.[num];
  return typeof r === 'number' ? r : 100;
}

/**
 * Optimiza un intercambio.
 *
 *   1. Build per-side pools (stickers they have that I need, and vice
 *      versa).
 *   2. Group each pool by category (section:intro, section:museum,
 *      team). Within each category, sort by rarity ascending (rarest
 *      first — those are the highest-value bargaining chips).
 *   3. Greedy match in priority order:
 *        - First, pair same-category swaps (intro↔intro, museum↔museum,
 *          team↔team) up to whatever the smaller pool of each category
 *          can support.
 *        - Then, with the leftovers on both sides, pair them across
 *          categories so a museum FWC can still trade for an intro FWC
 *          when no same-bucket match exists.
 *   4. Cap at `maxItems` per side.
 *
 * Falls back to a flat rarity-sorted match when `stickerByNumber` is
 * not provided (back-compat for callers that haven't been updated yet).
 */
export function optimizeTrade(myCollected, myDuplicates, theirCollected, theirDuplicates, rarityMap, maxItems = 10, stickerByNumber = null) {
  const iCanReceive = [];
  Object.keys(theirDuplicates).forEach(num => {
    if (theirDuplicates[num] > 0 && !myCollected[num]) iCanReceive.push(num);
  });

  const theyCanReceive = [];
  Object.keys(myDuplicates).forEach(num => {
    if (myDuplicates[num] > 0 && !theirCollected[num]) theyCanReceive.push(num);
  });

  // Back-compat: no sticker metadata → fall back to the old "sort
  // globally by rarity" behaviour. Callers without category info still
  // get a usable result, just without same-section prioritization.
  if (!stickerByNumber) {
    const sortedReceive = iCanReceive.sort((a, b) => rarityOf(rarityMap, a) - rarityOf(rarityMap, b));
    const sortedGive = theyCanReceive.sort((a, b) => rarityOf(rarityMap, a) - rarityOf(rarityMap, b));
    const count = Math.min(sortedReceive.length, sortedGive.length, maxItems);
    return { receive: sortedReceive.slice(0, count), give: sortedGive.slice(0, count) };
  }

  const bucketize = (nums) => {
    const buckets = {};
    nums.forEach(n => {
      const cat = categoryOfSticker(stickerByNumber[n]);
      if (!buckets[cat]) buckets[cat] = [];
      buckets[cat].push(n);
    });
    Object.values(buckets).forEach(arr => arr.sort((a, b) => rarityOf(rarityMap, a) - rarityOf(rarityMap, b)));
    return buckets;
  };

  const recvBuckets = bucketize(iCanReceive);
  const giveBuckets = bucketize(theyCanReceive);

  const receive = [];
  const give = [];

  // Phase 1: same-category greedy match, processed in priority order.
  //
  // Order matters because `maxItems` caps the final output. If we
  // exhausted the 25-strong `team` bucket before adding the lone
  // section:museum match (FWC 10 ↔ FWC 12), the cap would slice the
  // museum pair OFF and the user would never see their valuable FWC
  // match — exactly the bug reported in the screenshot.
  //
  // Section swaps (FWC intro/museum) are rarer and more meaningful per
  // the user's request, so they always come first in the output array.
  const CATEGORY_PRIORITY = ['section:intro', 'section:museum'];
  const orderedCats = [
    ...CATEGORY_PRIORITY.filter(c => recvBuckets[c] || giveBuckets[c]),
    ...Object.keys({ ...recvBuckets, ...giveBuckets })
      .filter(c => !CATEGORY_PRIORITY.includes(c)),
  ];
  orderedCats.forEach(cat => {
    const r = recvBuckets[cat] || [];
    const g = giveBuckets[cat] || [];
    const pairs = Math.min(r.length, g.length);
    for (let i = 0; i < pairs; i++) {
      receive.push(r[i]);
      give.push(g[i]);
    }
    // Consume what was paired; leftovers stay in the bucket for phase 2.
    recvBuckets[cat] = r.slice(pairs);
    giveBuckets[cat] = g.slice(pairs);
  });

  // Phase 2: cross-category fallback — flatten leftovers, sort by
  // rarity, and pair what we can. Without this the user's "FWC 1 in
  // intro vs friend's FWC 12 in museum" case would be ignored even
  // though they're both valuable to trade.
  const leftoverR = Object.values(recvBuckets).flat().sort((a, b) => rarityOf(rarityMap, a) - rarityOf(rarityMap, b));
  const leftoverG = Object.values(giveBuckets).flat().sort((a, b) => rarityOf(rarityMap, a) - rarityOf(rarityMap, b));
  const crossPairs = Math.min(leftoverR.length, leftoverG.length);
  for (let i = 0; i < crossPairs; i++) {
    receive.push(leftoverR[i]);
    give.push(leftoverG[i]);
  }

  const cap = Math.min(receive.length, maxItems);
  return { receive: receive.slice(0, cap), give: give.slice(0, cap) };
}

/**
 * Ordena una lista de amigos basada en el potencial de intercambio.
 * @param {Array} friendsWithStats - Lista de amigos con sus estadísticas calculadas.
 * @returns {Array}
 */
export function sortFriendsByPotential(friendsWithStats) {
  return [...friendsWithStats].sort((a, b) => {
    // Priorizar amigos con los que puedo RECIBIR más
    if (b.match.receive.length !== a.match.receive.length) {
      return b.match.receive.length - a.match.receive.length;
    }
    // Luego por puntuación total
    return b.match.score - a.match.score;
  });
}
